import type { ChinaMapPlace, ChinaMapRegionId } from "@-/dev.improve.haniverse.data.china-map";

/** What one map's places are called. The maps share their logic; only these words differ. */
export interface ChinaMapRegionCopy {
  /** What a top-level region's places are called, e.g. in "3 of 21 cities unlocked". */
  getPlaceNoun: (province: ChinaMapPlace) => { plural: string; singular: string };
  /** The tab's label. */
  label: string;
  /** The region in a sentence, e.g. "Show all of mainland China". */
  name: string;
  /** The overview's count of unlocked places, e.g. "Cities & districts unlocked". */
  placesLabel: string;
  /** The overview's count of unlocked top-level regions, e.g. "Provinces unlocked". */
  provincesLabel: string;
  /** The title over the top-level regions and places the learner will unlock soonest. */
  upcomingPlacesTitle: string;
}

const CITIES = { plural: "cities", singular: "city" };
const DISTRICTS = { plural: "districts", singular: "district" };
const TOWNSHIPS = { plural: "townships", singular: "township" };
const PARISHES = { plural: "parishes", singular: "parish" };
const PLANNING_AREAS = { plural: "planning areas", singular: "planning area" };

export const CHINA_MAP_REGION_COPY: Record<ChinaMapRegionId, ChinaMapRegionCopy> = {
  "hong-kong": {
    getPlaceNoun: () => DISTRICTS,
    label: "Hong Kong",
    name: "Hong Kong",
    placesLabel: "Districts",
    provincesLabel: "Areas",
    upcomingPlacesTitle: "Upcoming Places",
  },
  macau: {
    getPlaceNoun: () => PARISHES,
    label: "Macau",
    name: "Macau",
    placesLabel: "Parishes",
    provincesLabel: "Areas",
    upcomingPlacesTitle: "Upcoming Places",
  },
  mainland: {
    // The four municipalities are divided into districts, not cities.
    getPlaceNoun: (province) => (province.category === "municipality" ? DISTRICTS : CITIES),
    label: "Mainland China",
    name: "mainland China",
    placesLabel: "Cities & districts",
    provincesLabel: "Provinces",
    upcomingPlacesTitle: "Upcoming Places",
  },
  singapore: {
    getPlaceNoun: () => PLANNING_AREAS,
    label: "Singapore",
    name: "Singapore",
    placesLabel: "Planning areas",
    provincesLabel: "Regions",
    upcomingPlacesTitle: "Upcoming Places",
  },
  taiwan: {
    // Counties are divided into townships; the municipalities and cities into districts.
    getPlaceNoun: (province) => (province.category === "county" ? TOWNSHIPS : DISTRICTS),
    label: "Taiwan",
    name: "Taiwan",
    placesLabel: "Townships & districts",
    provincesLabel: "Counties & cities",
    upcomingPlacesTitle: "Upcoming Places",
  },
};
